import React, { useState, useEffect, useMemo, useRef } from 'react';
import ExploreDetailLiuyao from './ExploreDetail_liuyao.jsx';
import ExploreDetailQimen from './ExploreDetail_qimen.jsx';
import ExploreDetailBazi from './ExploreDetail_bazi.jsx';
import { X, Clock, HelpCircle, Share2, User, Heart, Star, MessageCircle, Send, Award, ChevronDown, ChevronUp, Copy, Check, Users, Lock } from 'lucide-react';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { renderPillar, renderYaoLine, getWuXingColor, colorizeHtml, calculateWangXiang, calculateTransformRelation, calOriatTraState, getNaJiaWithColor } from '../liuyao/_helper_liuyao.jsx';
import { liuyaoApi } from '../../services/api_liuyao.js';
import { qimenApi } from '../../services/api_qimen.js';
import { baziApi } from '../../services/api_bazi.js';
import { apiService } from '../../services/api.js';
import RewardManagementModal from '../jifen/RewardManagementModal.jsx';
import { isAuthenticated } from '../../utils/auth.js';

// --- Helper Functions ---

// --- Sub Components ---

/**
 * 探索页详情弹窗 - 左右分栏布局
 */
const ExploreDetail = ({ item, bizType, onClose, onLikeUpdate, onFavoriteUpdate, onCommentUpdate }) => {
  const [chartData, setChartData] = useState(null);
  const [liuyaoResult, setLiuyaoResult] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [expandedReplies, setExpandedReplies] = useState({});
  const [copied, setCopied] = useState(false);
  // 赏金管理弹窗状态
  const [showRewardModal, setShowRewardModal] = useState(false);
  // 点赞和收藏状态
  const [isLiked, setIsLiked] = useState(item?.is_liked || false);
  const [isFavorited, setIsFavorited] = useState(item?.is_favorited || false);
  const [likesCount, setLikesCount] = useState(item?.likes_count || 0);
  const [favoritesCount, setFavoritesCount] = useState(item?.favorites_count || 0);
  const [interactionLoading, setInteractionLoading] = useState(false);
  
  // 关注状态
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  const liuyaoRef = useRef(null);
  const qimenRef = useRef(null);
  const isLoggedIn = !!localStorage.getItem('access_token');
  const isQiuzhu = item?.publish_type === 'qiuzhu';
  
  // 获取当前用户ID
  const currentUserId = (() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr).id;
      } catch { return null; }
    }
    return null;
  })();
  
  // 是否是自己的发布
  const isOwnPost = currentUserId && currentUserId === item?.publisher_id;

  // 处理点赞
  const handleLike = async () => {
    if (interactionLoading) return;
    
    if (!isAuthenticated()) {
      alert('请先登录');
      return;
    }
    
    setInteractionLoading(true);
    try {
      const result = await apiService.toggleLike(bizType, item.id);
      
      if (result.success) {
        setIsLiked(result.data.is_liked);
        setLikesCount(result.data.likes_count);
        if (onLikeUpdate) onLikeUpdate(item.id, result.data);
        
        // 触发声望数据刷新事件
        window.dispatchEvent(new CustomEvent('reputationUpdate'));
      } else {
        alert(result.error || '操作失败');
      }
    } catch (error) {
      console.error('点赞失败:', error);
    } finally {
      setInteractionLoading(false);
    }
  };

  // 处理收藏
  const handleFavorite = async () => {
    if (interactionLoading) return;
    
    if (!isAuthenticated()) {
      alert('请先登录');
      return;
    }
    
    setInteractionLoading(true);
    try {
      const result = await apiService.toggleFavorite(bizType, item.id);
      
      if (result.success) {
        setIsFavorited(result.data.is_favorited);
        setFavoritesCount(result.data.favorites_count);
        if (onFavoriteUpdate) onFavoriteUpdate(item.id, result.data);
      } else {
        alert(result.error || '操作失败');
      }
    } catch (error) {
      console.error('收藏失败:', error);
    } finally {
      setInteractionLoading(false);
    }
  };

  // 处理关注
  const handleFollow = async () => {
    if (followLoading || isOwnPost) return;
    
    if (!isAuthenticated()) {
      alert('请先登录');
      return;
    }
    
    setFollowLoading(true);
    try {
      const result = await apiService.toggleFollow(item.publisher_id);
      
      if (result.success) {
        setIsFollowing(result.data.is_following);
      }
    } catch (error) {
      console.error('关注操作失败:', error);
    } finally {
      setFollowLoading(false);
    }
  };

  // 处理付费解锁
  const handleUnlock = async () => {
    if (!isAuthenticated()) {
      alert('请先登录');
      return;
    }
    
    if (isOwnPost) {
      alert('不能解锁自己的内容');
      return;
    }
    
    if (!chartData?.charge_amount || chartData.charge_amount <= 0) {
      alert('此内容无需付费');
      return;
    }
    
    const confirmUnlock = window.confirm(`确定要支付 ${chartData.charge_amount} 积分解锁完整内容吗？`);
    if (!confirmUnlock) return;
    
    try {
      let response;
      if (bizType === 'liuyao') {
        response = await axios.post(`/api/liuyao/unlock/${item.id}`, {}, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        });
      } else if (bizType === 'qimen') {
        response = await axios.post(`/api/qimen/unlock/${item.id}`, {}, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        });
      } else if (bizType === 'bazi') {
        response = await axios.post(`/api/bazi/unlock/${item.id}`, {}, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        });
      }
      
      if (response.data.success) {
        // 更新数据，显示完整内容
        setChartData(prevData => ({
          ...prevData,
          ...response.data.data,
          master_record_truncated: false,  // 标记为未截断
          is_unlocked: true  // 标记为已解锁
        }));
        alert('解锁成功！');
        
        // 触发积分数据刷新事件
        window.dispatchEvent(new CustomEvent('balanceUpdate'));
      } else {
        alert(response.data.error || '解锁失败');
      }
    } catch (error) {
      console.error('解锁失败:', error);
      alert(error.response?.data?.error || '解锁失败，请重试');
    }
  };

  // 获取关注状态
  useEffect(() => {
    if (!item?.publisher_id || !isAuthenticated() || isOwnPost) return;
    
    const fetchFollowStatus = async () => {
      try {
        const result = await apiService.getFollowStatus(item.publisher_id);
        
        if (result.success) {
          setIsFollowing(result.isFollowing);
        }
      } catch (error) {
        console.error('获取关注状态失败:', error);
      }
    };
    fetchFollowStatus();
  }, [item?.publisher_id, isOwnPost, bizType]);

  // 加载卦例详情和评论
  useEffect(() => {
    if (!item?.id) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        // 获取认证头
        const headers = {};
        const token = localStorage.getItem('access_token');
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        let chartRes, commentsRes;
        if (bizType === 'liuyao') {
          [chartRes, commentsRes] = await Promise.all([
            axios.get(`/api/liuyao/${item.id}`, { headers }),
            axios.get(`/api/public/liuyao/${item.id}/comments`)
          ]);
        } else if (bizType === 'qimen') {
          [chartRes, commentsRes] = await Promise.all([
            axios.get(`/api/qimen/${item.id}`, { headers }),
            axios.get(`/api/public/qimen/${item.id}/comments`)
          ]);
        } else if (bizType === 'bazi') {
          [chartRes, commentsRes] = await Promise.all([
            axios.get(`/api/bazi/${item.id}`, { headers }),
            axios.get(`/api/public/bazi/${item.id}/comments`)
          ]);
        } else {
          // 默认六爻
          [chartRes, commentsRes] = await Promise.all([
            axios.get(`/api/liuyao/${item.id}`, { headers }),
            axios.get(`/api/public/liuyao/${item.id}/comments`)
          ]);
        }
        if (chartRes.data.success) {
          // 六爻特殊处理：需要调用计算接口获取排盘结果
          if (bizType === 'liuyao') {
            // 先设置原始数据（包含 master_record 等信息）
            setChartData(chartRes.data.data);
            
            // 然后调用计算接口获取排盘结果
            if (chartRes.data.data.gua_code && chartRes.data.data.qiguatime) {
              try {
                const guaCodeStr = String(chartRes.data.data.gua_code);
                const divinationNumbers = guaCodeStr.split('').map(Number);
                const qiguaDate = new Date(chartRes.data.data.qiguatime);
                const pad = (num, len = 2) => String(num).padStart(len, "0");
                const datetimeStr = `${qiguaDate.getFullYear()}${pad(qiguaDate.getMonth() + 1)}${pad(qiguaDate.getDate())}${pad(qiguaDate.getHours())}${pad(qiguaDate.getMinutes())}${pad(qiguaDate.getSeconds())}`;
                const calcResponse = await axios.post('/api/liuyao/calculate', {
                  datetimeStr,
                  divinationNumbers
                });
                if (calcResponse.data.success) {
                  setLiuyaoResult(calcResponse.data.data);
                }
              } catch (calcError) {
                console.error('计算六爻失败:', calcError);
              }
            }
          }
          // 奇门特殊处理：后台已完成排盘，直接使用返回的数据
          else if (bizType === 'qimen') {
            // 合并排盘数据和表单数据
            const qimenData = {
              ...chartRes.data.data,
              // 从form中解析命主和求占信息（如果form存在）
              ...(chartRes.data.form && {
                name: chartRes.data.form.name,
                gender: chartRes.data.form.gender,
                type: chartRes.data.form.type,
                content: chartRes.data.form.content,
                // 生日信息
                birthYear: chartRes.data.form.birthYear,
                birthMonth: chartRes.data.form.birthMonth,
                birthDay: chartRes.data.form.birthDay,
                birthHour: chartRes.data.form.birthHour,
                // 预测时间
                predictYear: chartRes.data.form.predictYear,
                predictMonth: chartRes.data.form.predictMonth,
                predictDay: chartRes.data.form.predictDay,
                predictHour: chartRes.data.form.predictHour,
                predictMinute: chartRes.data.form.predictMinute,
                // 排盘方式
                qimenType: chartRes.data.form.qimenType,
                qimenMethod: chartRes.data.form.qimenMethod
              })
            };
            setChartData(qimenData);
          } else {
            // 其他类型（八字等）直接使用data
            setChartData(chartRes.data.data);
          }
        }
        if (commentsRes.data.success) {
          setComments(commentsRes.data.data || []);
        }
      } catch (error) {
        console.error('加载数据失败:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [item?.id, bizType]);

  // 提交评论
  const handleSubmitComment = async () => {
    if (!commentText.trim() || submitting) return;

    setSubmitting(true);
    try {
      const token = localStorage.getItem('access_token');
      let apiEndpoint;
      if (bizType === 'liuyao') {
        apiEndpoint = `/api/public/liuyao/${item.id}/comments`;
      } else if (bizType === 'qimen') {
        apiEndpoint = `/api/public/qimen/${item.id}/comments`;
      } else if (bizType === 'bazi') {
        apiEndpoint = `/api/public/bazi/${item.id}/comments`;
      } else {
        apiEndpoint = `/api/public/liuyao/${item.id}/comments`; // 默认六爻
      }

      const response = await axios.post(
        apiEndpoint,
        {
          content: commentText.trim(),
          parent_id: replyTo?.id || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // 刷新评论列表
        const commentsRes = await axios.get(apiEndpoint);
        if (commentsRes.data.success) {
          setComments(commentsRes.data.data || []);
          // 通知父组件更新评论数量
          if (onCommentUpdate) onCommentUpdate(item.id, commentsRes.data.data?.length || 0);
        }
        setCommentText('');
        setReplyTo(null);
        
        // 触发声望数据刷新事件
        window.dispatchEvent(new CustomEvent('reputationUpdate'));
      }
    } catch (error) {
      alert('评论失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  // 格式化时间
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const date = new Date(timeStr);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 切换回复展开
  const toggleReplies = (commentId) => {
    setExpandedReplies(prev => ({
      ...prev,
      [commentId]: !prev[commentId]
    }));
  };

  // 处理六爻数据
  const processedData = useMemo(() => {
    if (!liuyaoResult) return null;

    // 后端返回的是 dateInfo (驼峰)，兼容两种格式
    const dateInfo = liuyaoResult.dateInfo || liuyaoResult.date_info || {};
    const original = liuyaoResult.ben_gua || {};
    const changing = liuyaoResult.changing_yao || {};
    const transformed = liuyaoResult.bian_gua || {};

    // 从 chartData 获取占类、占题、属地等信息
    const zhanlei = chartData?.zhanti_type || item?.zhanti_type || "—";
    const zhanti = chartData?.zhanti || item?.zhanti || "—";
    const shudi = chartData?.shudi || "—";
    
    // 计算本命年柱（如果有出生年份信息）
    let birthYearPillar = null;
    // 注：如果后端返回了 birthYearPillar 可直接使用
    // 这里暂时不做复杂计算，可根据需要扩展

    const baseResult = {
      zhanlei,
      zhanti,
      shudi,
      birthYearPillar,
      yearPillar: dateInfo.yearPillar || "—",
      monthPillar: dateInfo.monthPillar || "—",
      dayPillar: dateInfo.dayPillar || "—",
      hourPillar: dateInfo.hourPillar || "—",
      dayPillarkongwang: dateInfo.dayPillarkongwang || "",
      datetimeDisplayStr: dateInfo.datetimeStrformat || "—",
    };

    const originalResult = {
      originalCodeSymbol: original.ben_gua_dongyao || [],
      originalLiuShen: original.liu_shen || [],
      originalFuShen: original.fu_shen || [],
      originalNaJia: original.stems?.map((s, i) => `${s}${original.branches?.[i] ?? ""}`) || (original.na_jia || []),
      originalLiuqin: original.liu_qin || [],
      changingPositions: changing.positions || [],
      shiPos: Number(original.shi_yao) || null,
      originalPalace: original.palace || original.palace_name || "",
      originalShiType: original.shi_type || "",
      originalMonthGuaShen: original.month_gua_shen || "",
      originalGuaName: original.name || original.gua_name || ""
    };

    const transformedResult = {
      transformedCodeArr: transformed.gua_arr || [],
      transformedLiuqin: transformed.liu_qin || [],
      transformedNaJia: transformed.stems?.map((s, i) => `${s}${transformed.branches?.[i] ?? ""}`) || (transformed.na_jia || []),
      transformedPalace: transformed.palace || transformed.palace_name || "",
      transformedShiType: transformed.shi_type || "",
      transformedGuaName: transformed.name || transformed.gua_name || ""
    };

    const hasChangedGua = originalResult.changingPositions.length > 0;
    const yingPos = originalResult.shiPos ? ((originalResult.shiPos + 3) > 6 ? originalResult.shiPos + 3 - 6 : originalResult.shiPos + 3) : null;

    const originalNaJia_wangshuai = calculateWangXiang(
      baseResult.dayPillar,
      baseResult.monthPillar,
      originalResult.originalNaJia
    );

    const transformedNaJia_wangshuai = calculateWangXiang(
      baseResult.dayPillar,
      baseResult.monthPillar,
      transformedResult.transformedNaJia
    );

    const transformRelations = calculateTransformRelation(
      originalResult.originalNaJia,
      transformedResult.transformedNaJia
    );

    const transformStates = calOriatTraState(
      originalResult.originalNaJia,
      transformedResult.transformedNaJia,
      baseResult.dayPillar
    );

    const guaTableData = Array.from({ length: 6 }).map((_, idx) => {
      const codeSymbol = originalResult.originalCodeSymbol || [];
      const symbol = codeSymbol[idx] != null ? codeSymbol[idx] : "—";
      const liuShen = originalResult.originalLiuShen[idx] || "";
      const liuqin = originalResult.originalLiuqin[idx] || "";
      const fuShen = originalResult.originalFuShen[idx] || "";
      const naJia = originalResult.originalNaJia[idx] || "";
      const wangXiang = originalNaJia_wangshuai ? originalNaJia_wangshuai[idx] : null;
      const transformedWangXiang = transformedNaJia_wangshuai ? transformedNaJia_wangshuai[idx] : null;
      const transformRelation = transformRelations ? transformRelations[idx] : "　";
      const transformState = transformStates ? transformStates[idx] : "";

      let shiYingLabel = "";
      if (originalResult.shiPos && idx === (originalResult.shiPos - 1)) shiYingLabel = "世";
      else if (yingPos && idx === (yingPos - 1)) shiYingLabel = "应";

      const transformedCodeArr = transformedResult.transformedCodeArr || [];
      const transformedSymbol = transformedCodeArr[idx];
      const transformedNaJia = transformedResult.transformedNaJia[idx] || "";
      const transformedliuqin = transformedResult.transformedLiuqin[idx] || "";

      return {
        liuShen,
        fuShen,
        symbol,
        naJia,
        wangXiang,
        transformedWangXiang,
        transformRelation,
        transformState,
        shiYing: shiYingLabel,
        liuqin,
        transformedliuqin,
        transSymbol: transformedSymbol,
        transformedNaJia,
        idx,
        isChanging: Array.isArray(originalResult.changingPositions) && originalResult.changingPositions.includes(idx)
      };
    });

    return {
      baseResult,
      originalResult,
      transformedResult,
      guaTableData,
      hasChangedGua
    };
  }, [liuyaoResult, chartData, item]);

  // 复制功能
  const handleCopy = async () => {
    const targetRef = bizType === 'liuyao' ? liuyaoRef : bizType === 'qimen' ? qimenRef : null;
    if (!targetRef?.current) return;
    
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);

    try {
      const container = document.createElement("div");
      container.style.position = "absolute";
      container.style.top = "-9999px";
      container.style.left = "-9999px";
      const mobileWidth = 430; 
      container.style.width = `${mobileWidth}px`;
      container.style.backgroundColor = "#ffffff"; 
      container.style.display = "flex";
      container.style.flexDirection = "column";
      container.style.padding = "16px";
      container.style.fontFamily = getComputedStyle(document.body).fontFamily;
      
      const contentClone = targetRef.current.cloneNode(true);
      contentClone.style.boxShadow = "none";
      contentClone.style.border = "none";
      contentClone.style.margin = "0";
      contentClone.style.width = "100%";
      contentClone.style.overflow = "visible";
      
      const scrollContainers = contentClone.querySelectorAll(".overflow-x-auto");
      scrollContainers.forEach(el => el.style.overflow = "visible");

      const buttons = contentClone.querySelectorAll("button");
      buttons.forEach(btn => btn.style.display = "none");

      container.appendChild(contentClone);
      document.body.appendChild(container);

      const scale = 3;
      const canvas = await html2canvas(container, {
        useCORS: true,
        scale: scale, 
        backgroundColor: "#ffffff"
      });
      
      document.body.removeChild(container);
      
      const footerHeight = 60;
      const scaledFooterHeight = footerHeight * scale; 
      
      const finalCanvas = document.createElement("canvas");
      finalCanvas.width = canvas.width;
      finalCanvas.height = canvas.height + scaledFooterHeight;
      const ctx = finalCanvas.getContext("2d");
      
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
      ctx.drawImage(canvas, 0, 0);
      
      const fontSize = 14 * scale;
      ctx.font = `${fontSize}px sans-serif`; 
      ctx.fillStyle = "#9ca3af";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      ctx.fillText("圆運命理系统", finalCanvas.width / 2, canvas.height + 20 * scale);
      ctx.fillText("bazi.tryonebest.com", finalCanvas.width / 2, canvas.height + 40 * scale);
      
      finalCanvas.toBlob(async (blob) => {
        if (!blob) return;
        try {
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        } catch(e) {
          const link = document.createElement('a');
          link.href = finalCanvas.toDataURL("image/png");
          link.download = `${bizType}_result_${Date.now()}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          alert("自动复制失败，已为您下载生成的图片。");
        }
      }, "image/png");

    } catch (e) {
      console.error(e);
      alert("生成图片失败");
    }
  };

  if (!item) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto md:overflow-hidden flex flex-col">
        {/* 头部 */}
        <div className={`flex items-center justify-between px-6 py-4 border-b ${isQiuzhu ? 'bg-gradient-to-r from-blue-50 to-cyan-50' : 'bg-gradient-to-r from-green-50 to-emerald-50'}`}>
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${isQiuzhu ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
              {isQiuzhu ? <HelpCircle className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
              {isQiuzhu ? '求助' : '分享'}
            </span>
            {item.zhanti_type && (
              <span className="text-sm text-gray-500 bg-white px-2 py-1 rounded-full">
                {item.zhanti_type}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* 内容区域 - 左右分栏 */}
        <div className="flex-1 flex flex-col md:flex-row overflow-visible md:overflow-hidden">
          {/* 左侧 - 业务类型自适应详情 */}
          <div className="w-full md:w-1/2 border-b md:border-b-0 md:border-r overflow-visible md:overflow-y-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : (
              <div className="space-y-4">
                {bizType === 'liuyao' && processedData && (
                  <ExploreDetailLiuyao
                    processedData={processedData}
                    liuyaoRef={liuyaoRef}
                    handleCopy={handleCopy}
                    copied={copied}
                    handleLike={handleLike}
                    handleFavorite={handleFavorite}
                    isLiked={isLiked}
                    isFavorited={isFavorited}
                    likesCount={likesCount}
                    favoritesCount={favoritesCount}
                    interactionLoading={interactionLoading}
                  />
                )}
                {bizType === 'qimen' && chartData && (
                  <ExploreDetailQimen
                    chartData={chartData}
                    qimenRef={qimenRef}
                    publisherName={item?.publisher_name}
                    handleCopy={handleCopy}
                    copied={copied}
                    handleLike={handleLike}
                    handleFavorite={handleFavorite}
                    isLiked={isLiked}
                    isFavorited={isFavorited}
                    likesCount={likesCount}
                    favoritesCount={favoritesCount}
                    interactionLoading={interactionLoading}
                  />
                )}
                {bizType === 'bazi' && chartData && (
                  <ExploreDetailBazi
                    chartData={chartData}
                    handleLike={handleLike}
                    handleFavorite={handleFavorite}
                    isLiked={isLiked}
                    isFavorited={isFavorited}
                    likesCount={likesCount}
                    favoritesCount={favoritesCount}
                    interactionLoading={interactionLoading}
                  />
                )}
              </div>
            )}
          </div>

          {/* 右侧 - 发布信息和评论 */}
          <div className="w-full md:w-1/2 flex flex-col overflow-visible md:overflow-hidden">
            {/* 发布信息 */}
            <div className={`px-4 pt-3 pb-2 md:px-6 md:pt-6 md:pb-4 border-b ${isQiuzhu ? 'bg-blue-50/50' : 'bg-green-50/50'}`}>
              <div className="flex items-center gap-3 md:gap-4 mb-2 md:mb-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center">
                  <User className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-sm md:text-base text-gray-900 leading-tight">{item.publisher_name}</div>
                  <div className="text-[11px] md:text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                    <Clock className="w-3 h-3" />
                    {formatTime(item.publish_time)}
                  </div>
                </div>
                {/* 关注按钮 */}
                {!isOwnPost && (
                  <button
                    onClick={handleFollow}
                    disabled={followLoading}
                    className={`px-3 md:px-4 py-1 md:py-1.5 rounded-full text-xs md:text-sm font-semibold transition-all duration-200 shadow-sm whitespace-nowrap ${
                      isFollowing
                        ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        : 'bg-gradient-to-r from-red-500 to-pink-500 text-white hover:from-red-600 hover:to-pink-600'
                    } ${followLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {followLoading ? '...' : isFollowing ? '已关注' : '关注'}
                  </button>
                )}
              </div>

              {/* 悬赏/收费信息 */}
              {isQiuzhu && item.payment_amount > 0 && (
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-orange-700 px-3 md:px-4 py-2 rounded-lg mb-1 md:mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm md:text-base">悬赏积分：{item.payment_amount} 分</span>
                    {item.is_finished ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-600 rounded-full">
                        <Check className="w-3 h-3" />
                        已结束
                      </span>
                    ) : comments.length > 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 rounded-full">
                        <Clock className="w-3 h-3" />
                        进行中
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 rounded-full">
                        <HelpCircle className="w-3 h-3" />
                        等待回复
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {item.end_date && (
                      <span className="text-[11px] md:text-xs">截止：{new Date(item.end_date).toLocaleDateString('zh-CN')}</span>
                    )}
                    {isOwnPost && !item.is_finished && (
                      <button
                        onClick={() => setShowRewardModal(true)}
                        className="px-2.5 py-1 text-xs font-medium text-orange-600 bg-orange-200 rounded-full hover:bg-orange-300 transition-colors"
                      >
                        管理赏金
                      </button>
                    )}
                  </div>
                </div>
              )}
              {!isQiuzhu && item.charge_amount > 0 && (
                <div className="flex items-center justify-between bg-emerald-100 text-emerald-700 px-3 md:px-4 py-2 rounded-lg mb-1 md:mb-2">
                  <span className="font-semibold text-sm md:text-base">付费积分：{item.charge_amount} 分</span>
                  {/* 付费次数 - 分享类显示 */}
                  {chartData?.paid_count !== undefined && (
                    <div className="flex items-center gap-1 text-xs md:text-sm">
                      <Users className="w-4 h-4" />
                      <span>{chartData.paid_count || 0} 付费</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* 评论区 */}
            <div className="p-6 md:flex-1 md:overflow-y-auto">
              {/* 分享类显示 master_record */}
              {!isQiuzhu && chartData?.master_record && (
                <div className="mb-6 p-4 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl border border-emerald-100">
                  <h4 className="font-semibold text-emerald-800 mb-2 flex items-center gap-2">
                    <Award className="w-4 h-4" />
                    命理师解读
                    {chartData.free_content_ratio < 100 && (
                      <span className="text-xs font-normal text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">
                        免费阅读 {chartData.free_content_ratio}%
                      </span>
                    )}
                  </h4>
                  <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {chartData.master_record}
                    {(!chartData.is_unlocked && chartData.charge_amount > 0 && chartData.free_content_ratio < 100) && (
                      <div className="mt-3 pt-3 border-t border-emerald-200 flex items-center gap-2 text-emerald-600">
                        <Lock className="w-4 h-4" />
                        <span className="text-sm">付费后查看完整内容</span>
                        <button 
                          onClick={handleUnlock}
                          className="ml-auto px-3 py-1 bg-emerald-500 text-white text-xs rounded-full hover:bg-emerald-600 transition-colors"
                        >
                          ¥{chartData.charge_amount} 解锁全文
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <h3 className="font-semibold text-gray-900 mb-4">评论区</h3>

              {comments.length > 0 ? (
                <div className="space-y-4">
                  {comments
                    .filter(comment => !comment.parent_id) // 只显示主评论
                    .map(comment => {
                      // 获取这个评论的所有回复
                      const replies = comments.filter(c => c.parent_id === comment.id);
                      return (
                        <CommentItem
                          key={comment.id}
                          comment={{ ...comment, replies }}
                          isQiuzhu={isQiuzhu}
                          onReply={() => setReplyTo(comment)}
                          expanded={expandedReplies[comment.id]}
                          onToggleReplies={() => toggleReplies(comment.id)}
                          formatTime={formatTime}
                        />
                      );
                    })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>暂无评论，快来发表你的看法吧</p>
                </div>
              )}
            </div>

            {/* 评论输入框 */}
            <div className="border-t p-4 bg-gray-50">
              {isLoggedIn ? (
                <div>
                  {replyTo && (
                    <div className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded-lg mb-2 text-sm">
                      <span className="text-blue-600">回复 @{replyTo.user_name}</span>
                      <button onClick={() => setReplyTo(null)} className="text-gray-400 hover:text-gray-600">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder={replyTo ? `回复 @${replyTo.user_name}...` : "发表你的看法..."}
                      className="flex-1 px-4 py-2 border rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-300"
                      onKeyPress={(e) => e.key === 'Enter' && handleSubmitComment()}
                    />
                    <button
                      onClick={handleSubmitComment}
                      disabled={!commentText.trim() || submitting}
                      className="px-6 py-2 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <Send className="w-4 h-4" />
                      发送
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-3 text-gray-500">
                  <a href="/login" className="text-indigo-600 hover:underline">登录</a> 后即可发表评论
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 赏金管理弹窗 */}
      {showRewardModal && (
        <RewardManagementModal
          chartId={item?.id}
          paymentAmount={item?.payment_amount || 0}
          bizType={bizType}
          onClose={() => setShowRewardModal(false)}
        />
      )}
    </div>
  );
};

/**
 * 评论项组件
 */
const CommentItem = ({ comment, isQiuzhu, onReply, expanded, onToggleReplies, formatTime }) => {
  return (
    <div className="bg-white border rounded-xl p-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center flex-shrink-0">
          <User className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-gray-900">{comment.user_name}</span>
            {comment.is_confirmed && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-600 text-xs rounded-full">
                <Award className="w-3 h-3" />
                已获赏 ¥{comment.reward_amount}
              </span>
            )}
          </div>
          <p className="text-gray-700 text-sm leading-relaxed mb-2">{comment.content}</p>
          <div className="flex items-center gap-4 text-xs text-gray-400">
            <span>{formatTime(comment.comment_time)}</span>
            <button
              onClick={onReply}
              className="hover:text-indigo-600 transition-colors"
            >
              回复
            </button>
          </div>

          {/* 回复列表 */}
          {comment.replies && comment.replies.length > 0 && (
            <div className="mt-3">
              <button
                onClick={onToggleReplies}
                className="flex items-center gap-1 text-xs text-indigo-600 hover:underline"
              >
                {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {comment.replies.length} 条回复
              </button>
              {expanded && (
                <div className="mt-2 space-y-2 pl-4 border-l-2 border-gray-100">
                  {comment.replies.map(reply => (
                    <div key={reply.id} className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm text-gray-900">{reply.user_name}</span>
                        <span className="text-xs text-gray-400">{formatTime(reply.comment_time)}</span>
                      </div>
                      <p className="text-sm text-gray-700">{reply.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExploreDetail;
